/**
 * Il catalogo arriva sporco (00-PERIMETRO §6, 05-CONTRATTO-API §11).
 *
 * La pulizia è responsabilità del backend, ma l'app va scritta sapendo che qualcosa
 * passerà comunque. Non è tolleranza per il disordine: è il rifiuto di mostrare a un
 * cliente finale una schermata che sembra guasta.
 */

/**
 * Quanti caratteri al massimo un nome di canale può occupare.
 *
 * Serve contro il caso reale: un logo codificato in base64 lungo cinquemila caratteri al
 * posto di «Rai 1». Una stringa così non va mai consegnata com'è a un motore di
 * rendering del testo — su una WebView di un box da trenta euro è un blocco visibile.
 */
export const MAX_CHANNEL_NAME_LENGTH = 80;

const M3U_ATTRIBUTE = /\b(?:tvg-id|tvg-name|tvg-logo|tvg-shift|group-title|catchup|catchup-source|radio)\s*=\s*"[^"]*"/gi;
const SEPARATOR_ROW = /^[\s#=*\-_~.]+$|^[#=*\-_~]{2,}.*[#=*\-_~]{2,}$/;
const BASE64_ISH = /^[A-Za-z0-9+/=]{120,}$/;
const DATA_URI = /^data:[a-z]+\/[a-z0-9.+-]+;base64,/i;

/**
 * Una riga che non è un canale ma un separatore: `##### SPORT #####`.
 * Si riconosce e non si mostra come canale.
 */
export function isSeparatorRow(rawName: string): boolean {
  const trimmed = rawName.trim();
  if (trimmed.length === 0) return true;
  return SEPARATOR_ROW.test(trimmed);
}

/** Il nome è in realtà un'immagine finita nel campo sbagliato. */
export function looksLikeEncodedImage(rawName: string): boolean {
  const trimmed = rawName.trim();
  return DATA_URI.test(trimmed) || BASE64_ISH.test(trimmed);
}

export interface CleanedName {
  /** Cosa mostrare. Mai vuoto: se non resta niente si torna un segnaposto vuoto e
   *  `usable` dice che non è un nome. */
  readonly text: string;
  /** `false` quando la riga non è un canale, o quando del nome non resta niente di
   *  mostrabile. Chi chiama decide se nascondere la riga o mettere le iniziali. */
  readonly usable: boolean;
  /** Il nome è stato accorciato: l'interfaccia può dirlo invece di mozzarlo in silenzio. */
  readonly truncated: boolean;
}

/**
 * Ripulisce un nome di canale come arriva da una lista M3U.
 *
 * Nell'ordine: separatore → si scarta; attributi M3U → si toglie tutto ciò che precede
 * l'ultima virgola, che è la convenzione della riga `#EXTINF`; base64 → si tronca e si
 * dichiara inutilizzabile, perché cinquemila caratteri non sono un nome accorciato, sono
 * un campo sbagliato.
 */
export function cleanChannelName(rawName: string): CleanedName {
  const trimmed = rawName.trim();
  if (isSeparatorRow(trimmed)) {
    return { text: '', usable: false, truncated: false };
  }
  if (looksLikeEncodedImage(trimmed)) {
    return { text: '', usable: false, truncated: true };
  }

  let text = trimmed;
  if (M3U_ATTRIBUTE.test(text)) {
    M3U_ATTRIBUTE.lastIndex = 0;
    const lastComma = text.lastIndexOf(',');
    text = lastComma >= 0 ? text.slice(lastComma + 1) : text.replace(M3U_ATTRIBUTE, '');
    M3U_ATTRIBUTE.lastIndex = 0;
    text = text.trim();
  }

  if (looksLikeEncodedImage(text)) {
    return { text: '', usable: false, truncated: true };
  }
  if (text.length === 0) {
    return { text: '', usable: false, truncated: false };
  }
  if (text.length > MAX_CHANNEL_NAME_LENGTH) {
    return { text: text.slice(0, MAX_CHANNEL_NAME_LENGTH), usable: true, truncated: true };
  }
  return { text, usable: true, truncated: false };
}

/**
 * Le iniziali per il segnaposto, quando la locandina o il logo non c'è.
 * Mai un riquadro rotto (05-CONTRATTO-API §11).
 */
export function initialsOf(name: string, maxLetters = 2): string {
  const words = name
    .split(/[\s·|/\\-]+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
  if (words.length === 0) return '';
  return words
    .slice(0, maxLetters)
    .map((word) => (word[0] ?? '').toUpperCase())
    .join('');
}
